import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { modelCatalogReviewApi } from '@/services/api';
import type { ModelCatalogReview } from '@/types';

/** Query key for the review document; the Model write hooks invalidate it. */
export const MODEL_CATALOG_REVIEW_KEY = ['model-catalog-review'] as const;

/**
 * The Model Catalog review (task 1093): `GET /models/review`, platform staff
 * only. A load is not free — the service calls Anthropic, OpenAI and models.dev
 * behind short per-source caches and records provider facts on matched Model
 * rows — so the document is not refetched on window focus.
 *
 * `refresh()` sends `?refresh=true`, which bypasses the service's caches for
 * that one load, and writes the result into the same query.
 */
export const useModelCatalogReview = (options: { enabled?: boolean } = {}) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: MODEL_CATALOG_REVIEW_KEY,
    queryFn: () => modelCatalogReviewApi.get().then((res) => res.data.data),
    enabled: options.enabled ?? true,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });

  const refresh = useMutation({
    mutationFn: () => modelCatalogReviewApi.get(true).then((res) => res.data.data),
    onSuccess: (doc: ModelCatalogReview) => {
      queryClient.setQueryData(MODEL_CATALOG_REVIEW_KEY, doc);
    },
  });

  return {
    ...query,
    refresh: () => refresh.mutate(),
    isRefreshing: refresh.isPending,
    refreshError: refresh.error,
  };
};
